import { useEffect, useRef, useState } from "react";
import { countdownParts, pad, dropDate } from "./format";
import { storeApi } from "./storeApi";
import Icon from "./Icons";

// The site's own film grain (public/, so not bundled).
const GRAIN = { backgroundImage: "url(/images/grain.webp)" };

// Ticks once a second; calls onDone once when the target passes.
export function useCountdown(iso, onDone) {
  const [now, setNow] = useState(() => Date.now());
  const done = useRef(false);
  const cb = useRef(onDone);
  cb.current = onDone;
  useEffect(() => {
    if (!iso) return undefined;
    done.current = false;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [iso]);
  const parts = iso ? countdownParts(iso, now) : null;
  useEffect(() => {
    if (parts?.done && !done.current) {
      done.current = true;
      cb.current?.();
    }
  }, [parts?.done]);
  return parts;
}

// 03 : 14 : 05 : 22 with small labels. `size` = "xl" | "md" | "sm".
export function CountdownDigits({ to, onDone, size = "md" }) {
  const p = useCountdown(to, onDone);
  if (!p) return null;
  const units = [
    ["days", p.days],
    ["hours", p.hours],
    ["min", p.minutes],
    ["sec", p.seconds],
  ];
  return (
    <div className={`sh-cd sh-cd-${size}`} role="timer" aria-label={`${p.days} days ${p.hours} hours ${p.minutes} minutes left`}>
      {units.map(([label, n], i) => (
        <div key={label} className="sh-cd-unit">
          <span className="sh-cd-num">{pad(n)}</span>
          <span className="sh-cd-label">{label}</span>
          {i < units.length - 1 && <span className="sh-cd-sep" aria-hidden="true" />}
        </div>
      ))}
    </div>
  );
}

// "2d 14h", "5h 03m", "12m 08s" — for a card badge.
export function CountdownShort({ to, onDone }) {
  const p = useCountdown(to, onDone);
  if (!p) return null;
  if (p.days) return `${p.days}d ${pad(p.hours)}h`;
  if (p.hours) return `${p.hours}h ${pad(p.minutes)}m`;
  return `${p.minutes}m ${pad(p.seconds)}s`;
}

// A drop in someone's calendar is the strongest reminder there is.
export function calendarHref({ title, iso, url }) {
  const t = (d) => new Date(d).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const end = new Date(new Date(iso).getTime() + 3600000).toISOString();
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//The Creaks//Merch//EN",
    "BEGIN:VEVENT",
    `UID:${t(iso)}-creaks-drop@thecreaksofficial.com`,
    `DTSTAMP:${t(new Date().toISOString())}`,
    `DTSTART:${t(iso)}`,
    `DTEND:${t(end)}`,
    `SUMMARY:${title.replace(/[,;\n]/g, " ")}`,
    `URL:${url}`,
    `DESCRIPTION:${url}`,
    "BEGIN:VALARM",
    "TRIGGER:-PT15M",
    "ACTION:DISPLAY",
    `DESCRIPTION:${title.replace(/[,;\n]/g, " ")}`,
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

export function AddToCalendar({ title, iso, url }) {
  return (
    <a className="sh-textlink sh-cal" href={calendarHref({ title, iso, url })} download="the-creaks-drop.ics">
      <Icon name="calendar" size={16} /> Add to calendar
    </a>
  );
}

// "Email me when it drops" — one field, one button, a clear promise.
export function NotifyForm({ topic, cta = "Notify me", done = "You're on the list. We'll email you the moment it drops.", note = "One email when it's live. No spam.", compact }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setState("busy");
    setError(null);
    try {
      await storeApi.notify(email, topic);
      setState("done");
    } catch (err) {
      setError(err.message);
      setState("idle");
    }
  };
  if (state === "done") {
    return (
      <p className="sh-notify-done" role="status">
        <Icon name="check" size={16} /> {done}
      </p>
    );
  }
  return (
    <form className={`sh-notify${compact ? " compact" : ""}`} onSubmit={submit}>
      <label className="sh-sr" htmlFor={`notify-${topic}`}>
        Email
      </label>
      <input
        id={`notify-${topic}`}
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email"
        autoComplete="email"
        inputMode="email"
      />
      <button className="sh-btn" disabled={state === "busy"}>
        {state === "busy" ? "…" : cta}
      </button>
      {error ? <p className="sh-error sh-notify-note">{error}</p> : note && <p className="sh-notify-note">{note}</p>}
    </form>
  );
}

// The whole store hidden behind its first drop.
export function CountdownStage({ countdown, onLaunch }) {
  const [live, setLive] = useState(false);
  const title = countdown.title || "Something is coming";
  const launched = () => setLive(true);
  // Ask until the store is open (this page goes away then). The first ask is
  // spread a little so everyone waiting doesn't hit at once; later ones cover
  // a clock that runs a bit fast.
  useEffect(() => {
    if (!live) return undefined;
    let t = setTimeout(function ask() {
      onLaunch();
      t = setTimeout(ask, 8000);
    }, 800 + Math.random() * 2200);
    return () => clearTimeout(t);
  }, [live, onLaunch]);
  const url = typeof window !== "undefined" ? window.location.href : "https://thecreaksofficial.com/merch";
  return (
    <section className="sh-stage">
      {countdown.imageUrl && <img className="sh-stage-img" src={countdown.imageUrl} alt="" />}
      <div className="sh-stage-veil" />
      <div className="sh-grain" style={GRAIN} />
      <div className="sh-stage-inner">
        <p className="sh-eyebrow">The Creaks · Merch</p>
        <h1 className="sh-display sh-stage-title">{live ? "It's live" : title}</h1>
        {countdown.text && !live && <p className="sh-stage-text">{countdown.text}</p>}
        {live ? (
          <p className="sh-stage-when">Opening the store…</p>
        ) : (
          <>
            <CountdownDigits to={countdown.launchAt} onDone={launched} size="xl" />
            <p className="sh-stage-when">{dropDate(countdown.launchAt)} · Prague time</p>
            <NotifyForm topic="launch" cta="Notify me" />
            <AddToCalendar title={`The Creaks: ${title}`} iso={countdown.launchAt} url={url} />
          </>
        )}
      </div>
    </section>
  );
}
